import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Alert, Avatar, Box, Link, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { FileUpload, Lock } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { yupResolver } from '@hookform/resolvers/yup';

import { MAX_FILE_SIZE, VALID_IMAGE_FORMATS } from '@bandido/shared/constants';
import { GetMeDTO } from '@bandido/shared/dtos';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { UploadButton } from '@frontend/features/common/components/UploadButton';

import { ControlledTextField } from '../../common/components/ControlledTextField';
import { ControlledSelect } from '../../common/components/ControlledSelect';
import { countries } from '../../common/constants/countries';

import { usePatchMe } from '../mutations';
import { IEditUser, DocumentType } from '../types';
import { useAuth } from '../hooks/useAuth';

interface Props {
  initialData: GetMeDTO | null;
  switchToForm: () => void;
  onClose?: () => void;
}

const resolver = yupResolver(
  yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    phoneCountryCode: yup
      .string()
      .test((value) => value !== 'no-selection')
      .required(),
    phoneNumber: yup.string().required(),
    position: yup.string(),
    address1: yup.string().required(),
    address2: yup.string().optional(),
    country: yup
      .string()
      .test((value) => value !== 'no-selection')
      .required(),
    city: yup.string().required(),
    postalCode: yup.string().required(),
    documentType: yup.mixed<DocumentType>().oneOf(Object.values(DocumentType)).required(),
    documentNumber: yup.string().required(),
    agencyId: yup.string(),
  }),
);

export const ProfileEditor = ({ onClose, switchToForm, initialData }: Props) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { myProfile } = useAuth();

  const methods = useForm<IEditUser>({
    resolver,
  });
  const { control, handleSubmit, reset, setValue } = methods;

  const { mutate } = usePatchMe({
    onSuccess: () => {
      onClose && onClose();
    },
  });

  const onSubmit = async (data: IEditUser) => {
    setLoading(true);
    mutate(data);
  };

  const userImage = initialData?.profilePicture;

  useEffect(() => {
    reset();
    if (initialData) {
      setValue('firstName', initialData.firstName);
      setValue('lastName', initialData.lastName);
      setValue('position', initialData.position);
      setValue('phoneCountryCode', initialData.phoneCountryCode);
      setValue('phoneNumber', initialData.phoneNumber);
      setValue('address1', initialData.address1);
      setValue('address2', initialData.address2);
      setValue('phoneExtension', initialData.phoneExtension);
      setValue('country', initialData.country);
      setValue('city', initialData.city);
      setValue('postalCode', initialData.postalCode);
      setValue('documentType', initialData.documentType as DocumentType);
      setValue('documentNumber', initialData.documentNumber);
    }
    setShowError(false);
    setLoading(false);
  }, [reset, setShowError, setLoading, initialData]);

  const handleUploadProfilePicture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const [file] = event.target.files ?? [];
      if (!file) return;

      if (!VALID_IMAGE_FORMATS.includes(file.type)) {
        addToast({
          message: t('auth.editUser.profilePicture.invalidFileFormat'),
          color: 'error',
        });
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        addToast({
          message: t('auth.editUser.profilePicture.fileTooLarge'),
          color: 'error',
        });
        return;
      }

      const formData = new FormData();
      formData.append('profileImage', file);

      await client.post(`user/${myProfile?.id}/upload-profile-photo`, formData);
      queryClient.invalidateQueries([QueryKeys.getMyUserData]);
    } catch (_e) {
      addToast({
        message: t('auth.editUser.profilePicture.errorMessage'),
        color: 'error',
      });
    }
  };

  const handlePhoneExtensionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 3);
    setValue('phoneExtension', value);
  };

  return (
    <Box padding={2} maxWidth={700}>
      <Stack px={1} pb={1.5} gap={2.5}>
        <Box display="flex" flexDirection="column">
          <Typography variant="h6">{t('auth.editUser.title')}</Typography>
          <Typography variant="subtitle1" align="left" color="text.secondary">
            {t('auth.editUser.subtitle')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          <Avatar
            src={userImage ?? ''}
            sx={{
              height: 74,
              width: 74,
              svg: {
                width: 24,
              },
            }}
          />
          <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('auth.editUser.pictureFieldDescription')}
            </Typography>

            <UploadButton
              component="label"
              variant="outlined"
              onChange={handleUploadProfilePicture}
              startIcon={<FileUpload />}
            >
              {t('auth.editUser.uploadPicture')}
            </UploadButton>
          </Box>
        </Box>
      </Stack>
      <Typography display="flex" alignItems="center" padding={theme.spacing(2)} gap={theme.spacing(1)}>
        <Lock fontSize="inherit" color="primary" />
        <Link component="button" onClick={switchToForm}>
          {t('auth.changePassword.title')}
        </Link>
      </Typography>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {showError && (
            <Box mb={2}>
              <Alert severity="error">{t('auth.editUser.failMessage')}</Alert>
            </Box>
          )}
          <Box display="flex" flexDirection="column" gap={3}>
            <ControlledTextField
              required
              type="hidden"
              name="agencyId"
              control={control}
              value={myProfile?.agencyId}
              sx={{
                display: 'none',
              }}
            />
            <ControlledTextField
              name="firstName"
              required
              control={control}
              placeholder={t('auth.editUser.firstName.placeHolder')}
              size="small"
              errorMessage={t('auth.editUser.firstName.errorMessage')}
              fullWidth
              helperText={t('auth.editUser.firstName.helperText')}
            />
            <ControlledTextField
              name="lastName"
              required
              control={control}
              placeholder={t('auth.editUser.lastName.placeHolder')}
              size="small"
              errorMessage={t('auth.editUser.lastName.errorMessage')}
              fullWidth
              helperText={t('auth.editUser.lastName.helperText')}
            />
            <Box display="flex" flexDirection="row" gap={3}>
              <ControlledSelect
                required
                autoWidth
                size="small"
                control={control}
                variant="outlined"
                name="phoneCountryCode"
                placeholder={t('auth.editUser.phoneCountryCode.placeHolder')}
                errorMessage={t('auth.editUser.lastName.errorMessage')}
                defaultValue={'no-selection'}
                sx={{
                  minWidth: 200,
                }}
              >
                <MenuItem value={'no-selection'}>{t('auth.editUser.phoneCountryCode.placeHolder')}</MenuItem>
                {countries.map((item, index) => (
                  <MenuItem key={index} value={item.phoneCode}>
                    {item.name} (+{item.phoneCode})
                  </MenuItem>
                ))}
              </ControlledSelect>
              <ControlledTextField
                name="phoneNumber"
                required
                control={control}
                placeholder={t('auth.editUser.phoneNumber.placeHolder')}
                size="small"
                errorMessage={t('auth.editUser.phoneNumber.errorMessage')}
                fullWidth
              />
            </Box>
            <ControlledTextField
              name="phoneExtension"
              control={control}
              placeholder={t('auth.editUser.phoneExtension.placeHolder')}
              size="small"
              type="text"
              inputProps={{ pattern: '[0-9]{3}' }}
              onChange={handlePhoneExtensionChange}
              errorMessage={t('auth.editUser.phoneExtension.errorMessage')}
              fullWidth
              helperText={t('auth.editUser.phoneExtension.helperText')}
            />
            <ControlledTextField
              name="position"
              control={control}
              placeholder={t('auth.editUser.position.placeHolder')}
              size="small"
              type="text"
              errorMessage={t('auth.editUser.position.errorMessage')}
              fullWidth
            />
            <ControlledTextField
              name="address1"
              required
              control={control}
              placeholder={t('auth.editUser.address1.placeHolder')}
              size="small"
              errorMessage={t('auth.editUser.address1.errorMessage')}
              fullWidth
              helperText={t('auth.editUser.address1.helperText')}
            />
            <ControlledTextField
              name="address2"
              placeholder={t('auth.editUser.address2.placeHolder')}
              control={control}
              size="small"
              errorMessage={t('auth.editUser.address2.errorMessage')}
              fullWidth
              helperText={t('auth.editUser.address2.helperText')}
            />
            <Box display="flex" flexDirection="row" gap={3}>
              <ControlledSelect
                required
                control={control}
                size="small"
                variant="outlined"
                name="country"
                placeholder={t('auth.editUser.country.placeHolder')}
                errorMessage={t('auth.editUser.country.errorMessage')}
                defaultValue={'no-selection'}
                sx={{
                  minWidth: 200,
                }}
              >
                <MenuItem value={'no-selection'}>{t('auth.editUser.country.placeHolder')}</MenuItem>
                {countries.map((item, index) => (
                  <MenuItem key={index} value={item.code}>
                    {item.name}
                  </MenuItem>
                ))}
              </ControlledSelect>
              <ControlledTextField
                required
                control={control}
                name="city"
                size="small"
                placeholder={t('auth.editUser.city.placeHolder')}
                errorMessage={t('auth.editUser.city.errorMessage')}
              />
              <ControlledTextField
                required
                control={control}
                size="small"
                name="postalCode"
                placeholder={t('auth.editUser.postalCode.placeHolder')}
                errorMessage={t('auth.editUser.postalCode.errorMessage')}
              />
            </Box>
            <Box display="flex" flexDirection="row" gap={3}>
              <ControlledSelect
                required
                variant="outlined"
                name="documentType"
                size="small"
                placeholder={t('auth.editUser.documentType.helperText')}
                errorMessage={t('auth.editUser.documentType.errorMessage')}
                helperText={t('auth.editUser.documentType.helperText')}
                control={control}
                defaultValue={DocumentType.CI}
                sx={{
                  minWidth: 200,
                }}
              >
                <MenuItem value={DocumentType.CI}>{DocumentType.CI}</MenuItem>
                <MenuItem value={DocumentType.DNI}>{DocumentType.DNI}</MenuItem>
                <MenuItem value={DocumentType.PASSPORT}>{DocumentType.PASSPORT}</MenuItem>
              </ControlledSelect>
              <ControlledTextField
                required
                fullWidth
                control={control}
                size="small"
                name="documentNumber"
                placeholder={t('auth.editUser.documentNumber.placeHolder')}
                errorMessage={t('auth.editUser.documentNumber.errorMessage')}
                helperText={t('auth.editUser.documentNumber.helperText')}
              />
            </Box>
            <LoadingButton loading={loading} fullWidth type="submit" variant="contained" color="primary">
              {t('auth.editUser.applyChangesBtn')}
            </LoadingButton>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};
